import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { WebPlanDetails, WebPlanTier } from '../../../../constants/allowance';
import { SVGIconType } from '../../../../constants/svgIcon';
import { AppBar, LightSwipe, ProgressBar, SvgIcon } from '../../../Molecules';

import styles from './NotEnoughStorageTemplate.module.css';

type NotEnoughStorageTemplateProps = {
  tier: WebPlanTier | undefined;
  createdItemCount: number;
  onClickCloudStorage: () => void;
  onClickUpgrade?: () => void;
};

export const NotEnoughStorageTemplate = memo(
  ({
    tier,
    createdItemCount,
    onClickCloudStorage,
    onClickUpgrade,
  }: NotEnoughStorageTemplateProps) => {
    const { t } = useTranslation();

    const { storage } = WebPlanDetails[tier ?? 'tier0'];

    let title;
    let warning;
    if (createdItemCount >= storage) {
      title = 'Not Enough Storage';
      warning = 'Your storage allowance of the plan is used up.';
    } else {
      title = 'Storage Almost Full';
      warning = 'Not enough storage to create games.';
    }

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.title}>{t(title)}</div>
        <div className={styles.redRibbon}>{t(warning)}</div>
        <div className={styles.bodyRow}>
          <SvgIcon
            className={styles.rowIcon}
            name={SVGIconType.Storage}
            width={24}
            height={24}
          />
          <div className={styles.rowContent}>
            <p className={styles.rowTitle}>{t('JOYAJOY Cloud Storage')}</p>
            {createdItemCount !== undefined && (
              <>
                <ProgressBar
                  current={createdItemCount}
                  total={storage}
                  height="8px"
                  backgroundColor="#f2f2f2"
                  fillColor="#ff5959"
                />
                <p className={styles.outOfStorage}>
                  <Trans
                    i18nKey="Cloud Storage Usage"
                    values={{
                      n: createdItemCount,
                      total: storage,
                      percentage: `${Math.round(
                        (createdItemCount / storage) * 100,
                      )}%`,
                    }}
                  >
                    Usage:
                    <span className={styles.boldText}>250</span>
                    of 1000 items &#40;
                    <span className={styles.boldText}>25%</span>
                    &#41;
                  </Trans>
                </p>
              </>
            )}
          </div>
        </div>
        <div className={styles.content}>
          {t(
            onClickUpgrade
              ? 'You can manage your storage or upgrade your subscription plan.'
              : 'To release more storage, remove some items.',
          )}
        </div>
        <div
          className={styles.cloudStorageButton}
          onClick={onClickCloudStorage}
        >
          <SvgIcon
            name={SVGIconType.Storage}
            width={24}
            height={24}
            color={'#00a698'}
          />
          <div>{t('Cloud Storage')}</div>
        </div>
        {onClickUpgrade && (
          <div className={styles.upgradeButton} onClick={onClickUpgrade}>
            <SvgIcon
              name={SVGIconType.SubscriptionUpgrade}
              width={24}
              height={24}
              color={'#ffffff'}
            />
            <div>{t('Upgrade')}</div>
            <LightSwipe />
          </div>
        )}
      </div>
    );
  },
);
